/**
 * ClientCaptionFetch (contracts §4) — caption discovery/fetch/parse in the learner's own browser
 * session (content ADR: the server never touches captions on a user's behalf).
 *
 * Empirical facts (M3-PLAN "Empirical facts", 2026-08-16): a plain web page cannot fetch YouTube
 * caption text — `timedtext` answers an empty 200 without a signed URL, and `youtubei` is 403
 * cross-origin. So `fetchTrack` is implemented honestly (it tries `timedtext?fmt=srv1` with
 * credentials) but is EXPECTED to fail today; the Add wizard treats any failure as "drop into
 * paste/upload", which is always available. `listTracks` is best-effort via the IFrame player's
 * `captions` module. The parsers (`parseSrv1`, `parseUpload`) are the parts that always work.
 */
import type { CaptionTrack, ClientCaptionFetch, VideoResolveResponse } from '@nelnela/contracts';
import { normalizeCues, parseCaptions, type Cue } from '@nelnela/subtitles';
import type { YTPlayer } from './player/youtube';

export type { Cue };
export type CaptionVideo = Pick<VideoResolveResponse, 'provider' | 'externalId'>;

export type CaptionFetchFailure = 'empty' | 'network' | 'unparseable';

/** Why a fetch/parse produced no cues; the wizard maps `reason` to one calm line. */
export class CaptionFetchError extends Error {
  constructor(
    public reason: CaptionFetchFailure,
    message?: string,
  ) {
    super(message ?? `caption fetch failed: ${reason}`);
    this.name = 'CaptionFetchError';
  }
}

// ------------------------------------------------ srv1 (YouTube timedtext XML)

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
};

/** One pass of HTML entity decoding: named (the common set) + decimal/hex numeric references. */
export function unescapeHtml(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (whole, ent: string) => {
    if (ent[0] === '#') {
      const code =
        ent[1] === 'x' || ent[1] === 'X' ? parseInt(ent.slice(2), 16) : Number(ent.slice(1));
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole;
      try {
        return String.fromCodePoint(code);
      } catch {
        return whole;
      }
    }
    return NAMED_ENTITIES[ent] ?? whole;
  });
}

/** YouTube double-encodes entities in srv1 (`&amp;gt;` → `&gt;` → `>`), hence two passes. */
export function unescapeTwice(s: string): string {
  return unescapeHtml(unescapeHtml(s));
}

const SRV1_TEXT_RE = /<text\s+([^>]*?)>([\s\S]*?)<\/text>/g;
const ATTR_START_RE = /\bstart="([\d.]+)"/;
const ATTR_DUR_RE = /\bdur="([\d.]+)"/;

/** Loose sniff: does this look like a timedtext srv1 document? */
export function looksLikeSrv1(text: string): boolean {
  const head = text.slice(0, 2000);
  return /<transcript[\s>]/.test(head) || /<text\s+start="/.test(head);
}

/**
 * Parse timedtext `srv1` (`<text start="s" dur="s">…</text>`) into raw cues — the port of
 * `scripts/yt-srv1-to-srt.py`: seconds → ms (rounded), missing `dur` = 0 (normalizeCues turns a
 * zero-length cue into 500 ms), entities unescaped twice, newlines → spaces, empty cues dropped.
 * Cue windows overlap on auto captions; that is what the normalizer's overlap clamp is for.
 */
export function parseSrv1(xml: string): Cue[] {
  const cues: Cue[] = [];
  SRV1_TEXT_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = SRV1_TEXT_RE.exec(xml)) !== null) {
    const attrs = m[1];
    const start = ATTR_START_RE.exec(attrs)?.[1];
    if (start === undefined) continue;
    const dur = ATTR_DUR_RE.exec(attrs)?.[1] ?? '0';
    const text = unescapeTwice(m[2]).replace(/\n/g, ' ').trim();
    if (!text) continue;
    const startSec = Number(start);
    const durSec = Number(dur);
    if (!Number.isFinite(startSec) || !Number.isFinite(durSec)) continue;
    cues.push({
      startMs: Math.round(startSec * 1000),
      endMs: Math.round((startSec + durSec) * 1000),
      text,
    });
  }
  return cues;
}

// ------------------------------------------------ track discovery (IFrame player module)

interface YtTrackListEntry {
  languageCode?: string;
  languageName?: string;
  displayName?: string;
  kind?: string;
  vss_id?: string;
  is_translateable?: boolean;
}

/** Map one raw IFrame `captions.tracklist` entry to the wire CaptionTrack; null when unusable. */
export function trackFromYt(raw: unknown): CaptionTrack | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as YtTrackListEntry;
  const lang = typeof t.languageCode === 'string' ? t.languageCode.toLowerCase() : '';
  if (!lang) return null;
  const name =
    (typeof t.languageName === 'string' && t.languageName) ||
    (typeof t.displayName === 'string' && t.displayName) ||
    undefined;
  return { lang, kind: t.kind === 'asr' ? 'auto' : 'manual', ...(name ? { name } : {}) };
}

/** Georgian first, then manual before auto, then by lang; stable otherwise. */
export function sortTracks(tracks: CaptionTrack[]): CaptionTrack[] {
  const rank = (t: CaptionTrack) =>
    (t.lang === 'ka' || t.lang.startsWith('ka-') ? 0 : 2) + (t.kind === 'manual' ? 0 : 1);
  return tracks
    .map((t, i) => ({ t, i }))
    .sort((a, b) => rank(a.t) - rank(b.t) || a.t.lang.localeCompare(b.t.lang) || a.i - b.i)
    .map((x) => x.t);
}

const LIST_POLL_MS = 250;
const LIST_TIMEOUT_MS = 3000;

/**
 * Best-effort caption track discovery through the IFrame player: `loadModule('captions')` then
 * poll `getOption('captions', 'tracklist')` for up to ~3 s. Resolves `[]` on anything missing —
 * no player, no module API, an empty list, an exception. Never throws.
 */
export async function listTracks(
  video: CaptionVideo,
  player?: YTPlayer | null,
  opts: { timeoutMs?: number; pollMs?: number } = {},
): Promise<CaptionTrack[]> {
  if (video.provider !== 'youtube' || !player) return [];
  if (typeof player.loadModule !== 'function' || typeof player.getOption !== 'function') return [];
  const timeoutMs = opts.timeoutMs ?? LIST_TIMEOUT_MS;
  const pollMs = opts.pollMs ?? LIST_POLL_MS;
  try {
    player.loadModule('captions');
  } catch {
    return [];
  }
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    let list: unknown = null;
    try {
      list = player.getOption('captions', 'tracklist');
    } catch {
      list = null;
    }
    if (Array.isArray(list) && list.length > 0) {
      const tracks = list.map(trackFromYt).filter((t): t is CaptionTrack => t !== null);
      // Dedupe on (lang, kind) — the module sometimes lists a track twice.
      const seen = new Set<string>();
      return sortTracks(
        tracks.filter((t) => {
          const k = `${t.lang}|${t.kind}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        }),
      );
    }
    if (Date.now() >= deadline) return [];
    await new Promise((r) => setTimeout(r, pollMs));
  }
}

// ------------------------------------------------ track fetch (timedtext srv1)

const FETCH_TIMEOUT_MS = 8000;

/** The timedtext URL for one track — exported so the failure line can point at what was tried. */
export function timedtextUrl(
  video: CaptionVideo,
  lang: string,
  kind?: CaptionTrack['kind'],
): string {
  const p = new URLSearchParams({ v: video.externalId, lang, fmt: 'srv1' });
  if (kind === 'auto') p.set('kind', 'asr');
  return `https://www.youtube.com/api/timedtext?${p.toString()}`;
}

/**
 * Fetch one caption track in the learner's session and parse it. EXPECTED TO FAIL for a plain
 * web page today (unsigned timedtext → empty 200): throws CaptionFetchError('empty') then, and
 * ('network') on any transport failure/timeout/non-2xx. Only ever resolves with ≥1 cue.
 */
export async function fetchTrack(
  video: CaptionVideo,
  lang: string,
  kind?: CaptionTrack['kind'],
): Promise<Cue[]> {
  if (video.provider !== 'youtube') throw new CaptionFetchError('network', 'unsupported provider');
  let text: string;
  try {
    const res = await fetch(timedtextUrl(video, lang, kind), {
      credentials: 'include',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!res.ok) throw new CaptionFetchError('network', `timedtext ${res.status}`);
    text = await res.text();
  } catch (e) {
    if (e instanceof CaptionFetchError) throw e;
    throw new CaptionFetchError('network', e instanceof Error ? e.message : String(e));
  }
  if (!text.trim()) throw new CaptionFetchError('empty');
  const cues = parseSrv1(text);
  if (cues.length === 0) throw new CaptionFetchError('empty');
  return cues;
}

// ------------------------------------------------ paste / upload

/**
 * Parse a pasted/uploaded caption file: SRT or VTT (autodetected by `parseCaptions`), or a raw
 * timedtext srv1 XML. Throws CaptionFetchError('unparseable') when nothing yields a cue.
 */
export async function parseUpload(fileOrText: Blob | string): Promise<Cue[]> {
  const text = typeof fileOrText === 'string' ? fileOrText : await fileOrText.text();
  return parseCaptionText(text);
}

/** Synchronous core of `parseUpload` (pasted text; the wizard's live "N lines" preview). */
export function parseCaptionText(text: string): Cue[] {
  const cues = looksLikeSrv1(text) ? parseSrv1(text) : parseCaptions(text);
  if (cues.length === 0) throw new CaptionFetchError('unparseable');
  return cues;
}

/** Client-side preview count ("N lines"); the server re-normalizes authoritatively on insert. */
export function previewCount(cues: Cue[]): number {
  return normalizeCues(cues).length;
}

/** The contract object, for callers that want the interface rather than the functions. */
export const clientCaptionFetch: ClientCaptionFetch = {
  listTracks: (video) => listTracks(video),
  fetchTrack: (video, lang) => fetchTrack(video, lang),
  parseUpload,
};
